/**
 * @module app.um
 */

import { autoinject, singleton, computedFrom, Disposable, observable, PLATFORM } from 'aurelia-framework';
import { Router, RouterConfiguration, RouteConfig, NavigationInstruction, NavModel } from 'aurelia-router';
import { ComponentBase, StandardLoadOptions, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { AuthService } from 'app/um/services/auth-service';
import { apiCall } from 'app/shared/lib/api/api';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthReloadEvent, AuthHeaderChangeEvent, AuthChangeEvent, AuthChangeCustomEvent } from 'app/um/lib/events';
import { IAppRouteConfig } from 'app/shared/config/router-types';
import { IQueueParameters, IQueueNavigationBinding } from 'app/core/models/essette-interfaces';
import { AuthContext } from 'app/um/lib/auth-context';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import * as UM from 'app/um/models/essette-api';

export interface ICallApiIntervalTimer {
    completed: boolean;
    initiated: boolean;
    currentMS: number;
    previousMS: number;
    intervalMS: number;
    maxIntervalMS: number;
}

type TypeCode = 'UM.Routing' | 'UM.MyAuths' | 'FM.Document' | 'UM.Portal';

export interface IAuthPageParameters extends Partial<IQueueParameters> {
    authId: string;
    queueTypeCode?: TypeCode;
}

@autoinject()
export class AuthPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @observable
    protected auth: UM.Auth = {};
    protected memberAuths: ReadonlyArray<UM.Auth> = [];
    protected currentRoute: string = '';
    protected currentQueueTypeCode?: TypeCode;
    protected queueBinding: IQueueNavigationBinding | null = null;

    @computedFrom('auth', 'auth.id')
    private get authId(): string | null {
        return this.auth && this.auth.id || null;
    }

    @computedFrom('auth', 'auth.member', 'auth.member.id')
    private get memberId(): string | null {
        return this.auth && this.auth.member && this.auth.member.id || null;
    }

    private navigationCompletedEvent: Disposable | null = null;
    private subscriptions: Array<Subscription> = [];

    protected callApiIntervalTimer: ICallApiIntervalTimer = {
        currentMS: 0,
        previousMS: 0,
        completed: false,
        initiated: false,
        intervalMS: 400, // starting interval. Afterwards previousMS + currentMS
        maxIntervalMS: this.env.config.apiPolling.maxIntervalMilliseconds
    };

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        @singleton(true) private readonly context: AuthContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);

        // NJ: In case someone leaves "pp" in the environment-config, this will save someone 8 hours of head banging
        if(!(context instanceof AuthPageContext)) {
            this.logger.error('context is not of type AuthPageContext. Is "pp" enabled for the suite application?');
        }
    }

    protected async activate(params: IAuthPageParameters, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        if(params.authId && this.auth.id !== params.authId) {
            const previousMemberId = this.auth.member?.id;
            await this.context.configure(params.authId);

            this.auth = this.context.auth;
            const currentMemberId = this.auth.member?.id;
            // if member ID changes after context is updated, reload member auths to populate tabs above auth ribbon
            if(this.memberId && previousMemberId !== currentMemberId) {
                this.loadMemberAuths(this.memberId);
            }
        }

        this.currentQueueTypeCode = params.queueTypeCode;
        if(this.currentQueueTypeCode) {
            this.queueBinding = {
                id: params.authId,
                queueTypeCode: params.queueTypeCode,
                queueId: params.queueId,
                queueTargetId: params.queueTargetId,
                queueAction: params.queueAction?.toLowerCase(),
                authClassID: params.authClassID,
                sourceCode: params.sourceCode,
                documentsConfig: {
                    serviceRootUrl: 'um/auths',
                    targetType: 'UM.Auth'
                }
            } as IQueueNavigationBinding;
        }

        if(this.auth && routeConfig.navModel) {
            routeConfig.navModel.setTitle(`Authorization #${this.auth.number}`);
        }

        this.subscriptions.push(
            this.events.subscribe(AuthChangeEvent, (e: AuthChangeEvent) => {
                if(e.authReloaded) {
                    if(this.context.auth.member?.id && this.auth.member?.id !== this.context.auth.member?.id)
                        this.loadMemberAuths(this.context.auth.member?.id);

                    this.auth = this.context.auth;
                }
                this.checkLatestTransaction();
            }),
            this.events.subscribe(AuthHeaderChangeEvent, (e: AuthHeaderChangeEvent) => {
                this.auth = this.context.auth;
                this.events.publish(new AuthReloadEvent());
            })
        );
        this.callApiIntervalTimer.completed = false;
        this.callApiIntervalTimer.initiated = false;
        this.checkLatestTransaction();
    }

    protected attached(): void {
        this.navigationCompletedEvent = this.events.subscribe('router:navigation:complete', () => this.navigationComplete());
        this.navigationComplete();
    }

    protected detached(): void {
        if(this.navigationCompletedEvent) {
            this.navigationCompletedEvent.dispose();
            this.navigationCompletedEvent = null;
        }
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    ////****************** Events ***************************************************////
    protected navigationComplete(): void {
        const routeConfig = this.router.currentInstruction.config;
        const route = Array.isArray(routeConfig.route) ? routeConfig.route[0] : routeConfig.route;
        const tab = routeConfig.settings && (routeConfig.settings.tab as string) || '';
        if(routeConfig && (routeConfig.nav || tab)) {
            this.currentRoute = `/${tab || route}`;
        }
    }

    ////****************** Getters and Setters ************************************************///
    protected authNavigationTabs(auth: UM.Auth): NavModel[] {
        return this.router.navigation.filter(tab => {
            let retVal = false;
            if(tab.config.settings.isVisible) {
                // If Care Days, check if is admission, if not then return true
                retVal = tab.config.route === 'care-days' ? (auth.isAdmission ?? false) : true;
            }
            return retVal;
        });
    }


    ////****************** Functions ************************************************////
    protected generateTabLink(targetAuthId: string, targetRoute: string): string {
        const parentUrl = this.router.parent.generate('um.auth', { authId: targetAuthId });
        return parentUrl + targetRoute;
    }

    protected generateNavigationLink(routeHref: string): string {
        let returnLink = routeHref;
        const params: Record<string, string> = {};

        if(this.queueBinding) {
            if(this.queueBinding.queueTypeCode) {
                params.queueTypeCode = this.queueBinding.queueTypeCode;
            }
            if(this.queueBinding.queueId) {
                params.queueId = this.queueBinding.queueId;
            }
            if(this.queueBinding.queueTargetId) {
                params.queueTargetId = this.queueBinding.queueTargetId;
            }
            if(this.queueBinding.queueAction) {
                params.queueAction = this.queueBinding.queueAction;
            }
            if(this.queueBinding.authClassID) {
                params.authClassID = this.queueBinding.authClassID;
            }
        }

        return returnLink + (this.queueBinding ? '?' + new URLSearchParams(params).toString() : '');
    }

    public async handleAuthChange($event: AuthChangeCustomEvent): Promise<void> {
        this.reload(!$event.detail?.authReloaded, $event.detail?.triggerRules);
    }

    public async reload(reloadContext?: boolean, triggerRules?: boolean): Promise<void> {
        if(!!reloadContext && this.authId) {
            await this.context.refreshAuthSettings({ id: this.authId });
        }

        this.auth = this.context.auth;
        this.events.publish(new AuthChangeEvent(true));

        if(!!triggerRules) {
            this.context.triggerCorrespondenceRules();
        }
    }

    protected async configureRouter(config: RouterConfiguration, router: Router): Promise<void> {
        await this.accountContext.load();
        const routes: IAppRouteConfig[] = [
            { route: '', redirect: 'info' },
            // Tab Navigation
            {
                route: 'info', name: 'um.auth.info', moduleId: PLATFORM.moduleName('app/um/pages/auth/auth-info-page'), title: 'um.auth.info', nav: true,
                settings: { topic: 'um.auth.info', icon: 'ui:icons.info', isVisible: this.accountContext.hasRouteAccess('um.auth.info') }
            },
            {
                route: 'diags-services', name: 'um.auth.diags-services', moduleId: PLATFORM.moduleName('app/um/pages/auth/diags-services-page'), title: 'um.auth.diags-services', nav: true,
                settings: { topic: 'um.auth.diags-services', icon: 'ui:icons.diagnoses', isVisible: this.accountContext.hasRouteAccess('um.auth.diags-services') }
            },
            {
                route: 'notes', name: 'um.auth.notes', moduleId: PLATFORM.moduleName('app/um/pages/auth/auth-notes-page'), title: 'um.auth.notes', nav: true,
                settings: { topic: 'um.auth.notes', icon: 'ui:icons.note', isVisible: this.accountContext.hasRouteAccess('um.auth.notes') }
            },
            {
                route: 'assessments', name: 'um.auth.assessments', moduleId: PLATFORM.moduleName('app/um/pages/auth/auth-assessments-page'), title: 'um.auth.assessments', nav: true,
                settings: { topic: 'um.auth.assessments', icon: 'ui:icons.assessment', isVisible: this.accountContext.hasRouteAccess('um.auth.assessments') }
            },
            {
                route: 'correspondence', name: 'um.auth.correspondence', moduleId: PLATFORM.moduleName('app/um/pages/auth/auth-correspondence-page'), title: 'Correspondence', nav: true,
                settings: { topic: 'um.auth.correspondence', icon: 'ui:icons.correspondence.general', isVisible: this.accountContext.hasRouteAccess('um.auth.correspondence') }
            },
            {
                route: 'care-days', name: 'um.auth.care-days', moduleId: PLATFORM.moduleName('app/um/pages/auth/auth-care-days-page'), title: 'Admission / Care Days', nav: true,
                settings: { topic: 'um.auth.care-days', icon: 'ui:icons.bed', isVisible: this.accountContext.hasRouteAccess('um.auth.care-days') }
            },
            {
                route: 'status', name: 'um.auth.status', moduleId: PLATFORM.moduleName('app/um/pages/auth/auth-status-page'), title: 'Status', nav: true,
                settings: { topic: 'um.auth.status', icon: 'ui:icons.history', isVisible: this.accountContext.hasRouteAccess('um.auth.status') }
            },
            {
                route: 'documents', name: 'um.auth.documents', moduleId: PLATFORM.moduleName('app/um/pages/auth/auth-documents-page'), title: 'Documents', nav: true,
                settings: { topic: 'um.auth.documents', icon: 'ui:icons.document', isVisible: this.accountContext.hasRouteAccess('um.auth.documents') }
            },
            {
                route: 'routing', name: 'um.auth.routing', moduleId: PLATFORM.moduleName('app/um/pages/auth/auth-routing-page'), title: 'um.auth.routing', nav: true,
                settings: { topic: 'um.auth.routing', icon: 'ui:icons.send', isVisible: this.accountContext.hasRouteAccess('um.auth.routing') }
            }
        ];
        config.map(routes);

        config.mapUnknownRoutes(PLATFORM.moduleName('app/shared/components/not-found'));

        this.router = router;
    }

    protected getQueueNavigationView(): string | undefined {
        if(!this.queueBinding) { return; }
        const views = this.env.getDynamicViewsByKey('Core.QueueType', this.queueBinding.queueTypeCode) || [];

        if(!views[0]?.url) {
            this.logger.error(`No views found for Core.QueueType ${this.queueBinding.queueTypeCode}`);
            return;
        }
        return views[0].url;
    }

    protected async checkLatestTransactionCompleted(): Promise<void> {
        if(this.authId) {
            const response = await this.authService.loadLatestEagleTransactionByAuth(this.authId);
            const latestEagleTransaction = response.results[0];

            const completed = latestEagleTransaction.metadata?.completed?.timestamp;
            if(!completed) return;
            this.callApiIntervalTimer.completed = true;
            await this.reload(true, false);
            this.events.publish(new AuthReloadEvent());
        }
    }

    protected checkLatestTransaction(): void {
        const latestEagleTransaction = this.context.latestEagleTransaction;
        if(this.context.syncToClaimsSystem && latestEagleTransaction?.id) {

            const completed = latestEagleTransaction.metadata?.completed?.timestamp;

            if(!completed && !this.callApiIntervalTimer.initiated) {
                this.delayedRepeat(this.checkLatestTransactionCompleted, this.callApiIntervalTimer);
            }
        }
    }

    protected async delayedRepeat(fn: Function, timer: ICallApiIntervalTimer): Promise<void> {
        const initiated = timer.initiated;
        const intervalMS = timer.intervalMS;
        const maxMS = timer.maxIntervalMS;
        let previousMS = timer.previousMS;
        let currentMS = timer.currentMS;

        if(initiated === false) {
            timer.initiated = true;
            timer.currentMS = 0;
            timer.previousMS = 0;
        } else {
            currentMS = (currentMS <= 0) ? intervalMS : currentMS;
            const newTimeoutMS = previousMS + currentMS;
            timer.previousMS = currentMS;
            timer.currentMS = Math.min(newTimeoutMS, maxMS);
        }

        return new Promise((resolve) => {
            setTimeout(async () => {
                const thisFn = fn.bind(this);
                resolve(await thisFn());
                if(!timer.completed) {
                    this.delayedRepeat(fn, timer);
                } else {
                    timer.initiated = false;
                    timer.completed = false;
                    timer.previousMS = 0;
                    timer.currentMS = 0;
                }
            }, timer.currentMS);
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberAuths(memberId: string): Promise<void> {
        const sorting: Sorting = new Sorting();
        sorting.add({
            reference: 'metadata.created.timestamp',
            direction: SortDirection.Descending
        });

        const options: StandardLoadOptions = {
            sorting: sorting,
            includeInactive: false
        };

        const includes = [
            'assignedTo',
            'class',
            'class.subClass',
            'status'
        ];

        const fields = [
            'assignedTo.description',
            'class.code',
            'class.subClass.code',
            'class.subClass.description',
            'isClosed',
            'metadata.created.timestamp',
            'number',
            'status.description'
        ];

        try {
            this.state.beginLoad();
            const response = await this.authService.loadAuthsByMember(memberId, options, includes, fields);
            this.memberAuths = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
